import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { auditApi } from './api';
import { keys } from '@/lib/query/keys';
import { useListState } from '@/lib/hooks/useListState';
import { useGate } from '@/lib/rbac/Gate';
import { DataView, ForbiddenBanner } from '@/lib/ui/states';
import { DataTable, type Column } from '@/lib/ui/Table';
import { Pagination } from '@/lib/ui/Pagination';
import { PageHeader, MonoValue } from '@/lib/ui/Typography';
import { Tag } from '@/lib/ui/Tag';
import { TextInput } from '@/lib/ui/Field';
import { Dialog } from '@/lib/ui/Dialog';
import { formatDateTime } from '@/lib/format';
import type { AuditEntry } from '@/lib/api/types';

/** Pretty-print any JSON-ish value, falling back to a placeholder when empty. */
function prettyJson(value: unknown): string {
  if (value == null) return '—';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Audit log browser (spec §16 gap 13). Read-only feed of every recorded change, newest
 * first (ordering comes from the server). Row click opens the full entry, including a
 * pretty-printed diff.
 */
export function AuditPage() {
  const { t } = useTranslation();
  const { allowed } = useGate('audit:read');
  const list = useListState({
    entity_type: '',
    entity_id: '',
    actor_id: '',
    action: '',
    from: '',
    to: '',
  });
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  const query = useQuery({
    queryKey: keys.audit(list.params),
    queryFn: () => auditApi.list(list.params),
    placeholderData: (prev) => prev,
    enabled: allowed,
  });

  if (!allowed) {
    return (
      <div>
        <PageHeader eyebrow={t('audit.eyebrow')} title={t('audit.title')} />
        <ForbiddenBanner message={t('audit.forbidden')} />
      </div>
    );
  }

  const columns: Column<AuditEntry>[] = [
    {
      id: 'created_at',
      header: t('audit.colTime'),
      cell: (r) => <MonoValue className="text-ink-mid">{formatDateTime(r.created_at)}</MonoValue>,
    },
    {
      id: 'actor',
      header: t('audit.colActor'),
      cell: (r) => (
        <span className="text-ink-deep">{r.actor_email ?? r.actor_id ?? '—'}</span>
      ),
    },
    {
      id: 'action',
      header: t('audit.colAction'),
      cell: (r) => <Tag variant="neutral">{r.action}</Tag>,
    },
    {
      id: 'entity',
      header: t('audit.colEntity'),
      cell: (r) => (
        <span className="text-ink-mid">
          <MonoValue>{r.entity_type}</MonoValue>
          {r.entity_id ? (
            <>
              {' / '}
              <MonoValue className="text-ink-mute">{r.entity_id}</MonoValue>
            </>
          ) : null}
        </span>
      ),
    },
    {
      id: 'ip',
      header: t('audit.colIp'),
      cell: (r) => <MonoValue className="text-ink-mute">{r.ip ?? '—'}</MonoValue>,
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t('audit.eyebrow')}
        title={t('audit.title')}
        count={query.data?.meta?.pagination.total}
      />

      {/* Filters */}
      <div className="mb-md flex flex-wrap items-end gap-sm">
        <div className="w-[160px]">
          <TextInput
            label={t('audit.filterEntityType')}
            value={(list.filters.entity_type as string) || ''}
            onChange={(e) => list.setFilter('entity_type', e.target.value || undefined)}
          />
        </div>
        <div className="w-[200px]">
          <TextInput
            label={t('audit.filterEntityId')}
            value={(list.filters.entity_id as string) || ''}
            onChange={(e) => list.setFilter('entity_id', e.target.value || undefined)}
          />
        </div>
        <div className="w-[200px]">
          <TextInput
            label={t('audit.filterActorId')}
            value={(list.filters.actor_id as string) || ''}
            onChange={(e) => list.setFilter('actor_id', e.target.value || undefined)}
          />
        </div>
        <div className="w-[160px]">
          <TextInput
            label={t('audit.colAction')}
            value={(list.filters.action as string) || ''}
            onChange={(e) => list.setFilter('action', e.target.value || undefined)}
          />
        </div>
        <div className="w-[180px]">
          <TextInput
            type="date"
            label={t('audit.filterFrom')}
            value={(list.filters.from as string) || ''}
            onChange={(e) => list.setFilter('from', e.target.value || undefined)}
          />
        </div>
        <div className="w-[180px]">
          <TextInput
            type="date"
            label={t('audit.filterTo')}
            value={(list.filters.to as string) || ''}
            onChange={(e) => list.setFilter('to', e.target.value || undefined)}
          />
        </div>
      </div>

      <DataView query={query} isEmpty={(d) => d.data.length === 0}>
        {(d) => (
          <>
            <DataTable
              columns={columns}
              rows={d.data}
              rowKey={(r) => r.id}
              onRowClick={(r) => setSelected(r)}
            />
            {d.meta?.pagination && (
              <Pagination
                meta={d.meta.pagination}
                onPageChange={list.setPage}
                onPerPageChange={list.setPerPage}
              />
            )}
          </>
        )}
      </DataView>

      <Dialog
        open={selected !== null}
        onOpenChange={(open) => !open && setSelected(null)}
        title={t('audit.entryTitle')}
        description={selected ? formatDateTime(selected.created_at) : undefined}
        size="lg"
      >
        {selected && (
          <dl className="flex flex-col gap-md">
            <Detail label={t('audit.colAction')}>
              <Tag variant="neutral">{selected.action}</Tag>
            </Detail>
            <Detail label={t('audit.colActor')}>
              <span className="text-ink-deep">
                {selected.actor_email ?? selected.actor_id ?? '—'}
              </span>
              {selected.actor_email && selected.actor_id ? (
                <MonoValue className="ms-xs text-ink-mute">{selected.actor_id}</MonoValue>
              ) : null}
            </Detail>
            <Detail label={t('audit.colEntity')}>
              <MonoValue className="text-ink-mid">{selected.entity_type}</MonoValue>
              {selected.entity_id ? (
                <MonoValue className="ms-xs text-ink-mute">{selected.entity_id}</MonoValue>
              ) : null}
            </Detail>
            <Detail label={t('audit.colIp')}>
              <MonoValue className="text-ink-mute">{selected.ip ?? '—'}</MonoValue>
            </Detail>
            <Detail label={t('audit.detailUserAgent')}>
              <span className="break-all text-small text-ink-mid">
                {selected.user_agent ?? '—'}
              </span>
            </Detail>
            <Detail label={t('audit.detailDiff')}>
              <pre className="tnum max-h-[40vh] overflow-auto rounded-md border-[0.5px] border-hairline bg-canvas-bone p-md font-mono text-small text-ink-deep">
                {prettyJson(selected.diff)}
              </pre>
            </Detail>
          </dl>
        )}
      </Dialog>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2xs">
      <dt className="eyebrow text-ink-mute">{label}</dt>
      <dd className="flex flex-wrap items-center">{children}</dd>
    </div>
  );
}
